import PageHead from '@/components/common/PageHead';
import PageLayout from '@/components/common/pageLayout/PageLayout';
import PlentyFullScreenSpinner from '@/components/common/PlentyFullScreenSpinner';
import {
    ONBOARDING_POSITIONS,
    useOnboardingPosition,
} from '@/components/onboarding/useOnboardingPosition';
import { useIsMembershipEnded } from '@/services/billing/useIsMembershipEnded';
import { useIsPaymentRequired } from '@/services/billing/useIsPaymentRequired';
import { beginUserMasquerade } from '@/services/stytch';
import { useUserData } from '@/services/user';
import { setDatadogUser } from '@/util/datadog';
import { CurrentUserContext } from '@common/react/user';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { MobileSplash } from './common/MobileSplash';
import FeedbackWidget from './feedback/FeedbackWidget';

const AuthPageWrapper = ({ children }: { children: React.ReactNode }) => {
    const {
        isLoading,
        error,
        stytchUser,
        userInfo,
        activePartnershipId,
        partnerInfo,
        incomingPartnershipInvite,
        outgoingPartnershipInvite,
        isInvitedPartner,
        plentyJointAccountStatus,
        membership,
        featureFlags,
    } = useUserData();
    const onboardingPosition = useOnboardingPosition();
    const isPaymentRequired = useIsPaymentRequired();
    const isMembershipEnded = useIsMembershipEnded();
    const router = useRouter();
    const searchParams = useSearchParams();
    const masqueradeSessionId = searchParams.get('masquerade_session_id');

    // Resurrection: Only allow admin users or users with allowed email domains
    const userEmail = stytchUser?.emails?.[0]?.email ?? '';
    const isAllowedDomain =
        userEmail.endsWith('@withplenty.com') ||
        userEmail.endsWith('@wealthsimple.com');
    const shouldRedirectToMarketingSite =
        userInfo && !userInfo.isAdmin && !isAllowedDomain;

    useEffect(() => {
        if (shouldRedirectToMarketingSite) {
            window.location.href = 'https://withplenty.com';
        }
    }, [shouldRedirectToMarketingSite]);

    useEffect(() => {
        if (masqueradeSessionId) {
            fetch(
                `${process.env.NEXT_PUBLIC_PLENTY_API_URL}/public/masquerade-sessions/${masqueradeSessionId}`
            )
                .then((res) => res.json())
                .then((res) =>
                    beginUserMasquerade({
                        stytchSessionJwt: res.session_jwt,
                        stytchSession: res.session_token,
                    })
                )
                .then(() => {
                    window.location.href = '/';
                });
        }
    }, [masqueradeSessionId]);

    if (userInfo) {
        setDatadogUser(userInfo);
    }

    if (
        isLoading ||
        error ||
        !router.isReady ||
        shouldRedirectToMarketingSite
    ) {
        return <PlentyFullScreenSpinner />;
    }

    if (
        (userInfo?.plentyAccountStatus === 'CLOSING' ||
            userInfo?.plentyAccountStatus === 'CLOSED') &&
        router.route !== '/account-closure' &&
        !userInfo?.isAdmin
    ) {
        router.push('/account-closure');
        return <PlentyFullScreenSpinner />;
    }

    if (
        isMembershipEnded &&
        router.route !== '/account-closure' &&
        router.route !== '/membership-ended'
    ) {
        router.push('/membership-ended');
        return <PlentyFullScreenSpinner />;
    }

    if (
        isPaymentRequired &&
        router.route !== '/account-closure' &&
        router.route !== '/membership-ended' &&
        router.route !== '/free-trial-ended'
    ) {
        router.push('/free-trial-ended');
        return <PlentyFullScreenSpinner />;
    }

    const isUnauthenticated =
        onboardingPosition === 'unauthenticated' || !stytchUser;

    if (
        ONBOARDING_POSITIONS.includes(onboardingPosition) ||
        isUnauthenticated
    ) {
        // Encode path user intended to visit before redirecting them to signin.
        // This allows us to redirect them back to the intended page after auth.
        // Special-case on `signOut` query param to prevent redirecting user
        // back to the page they just signed out from.
        const signinPath =
            router.asPath !== '/' && !router.query.signOut
                ? `/signin?from=${encodeURIComponent(router.asPath)}`
                : '/signin';
        const as = isUnauthenticated ? signinPath : undefined;
        router.push('/onboarding', as);
        return <PlentyFullScreenSpinner />;
    }

    if (
        !onboardingPosition ||
        onboardingPosition === 'loading' ||
        !userInfo ||
        !featureFlags
    ) {
        return <LoadingPage />;
    }

    return (
        <>
            <PageHead />

            <CurrentUserContext.Provider
                value={{
                    userInfo,
                    activePartnershipId,
                    partnerInfo,
                    incomingPartnershipInvite,
                    outgoingPartnershipInvite,
                    isInvitedPartner,
                    plentyJointAccountStatus,
                    membership,
                    featureFlags,
                }}
            >
                <MobileSplash>{children}</MobileSplash>
            </CurrentUserContext.Provider>
            <FeedbackWidget isFixed />
        </>
    );
};

const LoadingPage = () => (
    <>
        <PageHead />
        <PageLayout isAppLoading />;
    </>
);

export default AuthPageWrapper;
